import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useInstallerAuthContext } from '../hooks/useInstallerAuthContext';
import * as d3 from 'd3';

import { selectMapByStore } from '../app/neptune/neptuneSlice';
import { selectLogicalNameCoordinates } from '../app/calibration/startCalibrationSlice';
import {
  hideDeptNames,
  createReaderVectors,
} from '../utils/tagCalibrationHelpers';

import EnterpriseIcon, {
  ChevronUpDoubleIcon,
  ChevronDownDoubleIcon,
} from '@enterprise-ui/icons';
import './styles/neptuneMapStyles.scss';

export const NeptuneMap = ({ storeId, calibrationState, direction }) => {
  const { user } = useInstallerAuthContext();
  const svgWrapper = useRef(null);

  const floorNum = useSelector((state) => state.neptune.storeFloor);
  const mapSelector = useSelector((state) =>
    selectMapByStore(state, storeId, floorNum),
  );

  const selectActiveReader = useSelector((state) => state.neptune.activeReader);
  const readerCoordinates = useSelector((state) =>
    selectLogicalNameCoordinates(state, storeId, floorNum),
  );
  const rfidReaders = useMemo(
    () => Object.keys(readerCoordinates),
    [readerCoordinates],
  );
  console.log(readerCoordinates, 'reader coordinates from store');
  console.log(rfidReaders, 'rfid readers from store');
  // console.log(rfidReaders);

  const [readerCoords, setReaderCoords] = useState({});
  const [readerVectors, setReaderVectors] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const [readersReady, setReadersReady] = useState(false);

  const zoom = d3
    .zoom()
    .scaleExtent([1, 3])
    .extent([
      [0, 0],
      [150, 100],
    ])
    .translateExtent([
      [0, 0],
      [150, 100],
    ]); // Set zoom scale + translate limits

  const d3Map = d3.select(svgWrapper.current); // Select main svg using D3

  const handleZoom = useCallback((e) => {
    const svgRoot = svgWrapper.current?.querySelector('svg');
    if (!svgRoot) return;

    d3.select(svgRoot).select('#content').attr('transform', e.transform);
  }, []);

  const focusOnReader = useCallback(
    (reader) => {
      if (!reader || !readerCoords?.[reader] || !svgWrapper.current) return;

      let { x, y } = readerCoords[reader];
      d3Map
        .transition()
        .duration(500)
        .call(
          zoom.transform,
          d3.zoomIdentity
            .translate(150, 100)
            .scale(3)
            .translate(-x + -30, -y + -10), // Takes into account neg values in viewbox from svg map
        );
    },
    [d3Map, readerCoords, zoom],
  );

  zoom.on('zoom', handleZoom); // Function to call on zoom
  d3Map.call(zoom); // Apply zoom behavior to the SVG map

  const addReaderToMap = useCallback((vector) => {
    console.log('Adding reader vector to map: ', vector);
    if (svgWrapper.current !== null) {
      let svgElem = svgWrapper.current.querySelector('svg #content');
      if (!svgElem) {
        return false;
      } else {
        svgElem.innerHTML += vector;
      }
    }
  }, []);

  useEffect(() => {
    if (!mapSelector || !svgWrapper.current) return;

    svgWrapper.current.innerHTML = mapSelector;

    const deptLabels = Array.from(
      svgWrapper.current.querySelectorAll('.adjacency-name'),
    );
    hideDeptNames(deptLabels);

    if (user) {
      const mapWrapper = document.querySelector('div.mapWrapper');
      mapWrapper?.classList.add('externalLayer');
    }

    setMapReady(true);
    setReadersReady(false);
  }, [mapSelector, user]);

  useEffect(() => {
    if (!readerCoordinates || Object.keys(readerCoordinates).length === 0) {
      setReaderCoords({});
      setReaderVectors([]);
      setReadersReady(false);
      return;
    }

    const vectors = createReaderVectors(readerCoordinates);
    setReaderCoords(readerCoordinates);
    setReaderVectors(vectors);
  }, [readerCoordinates]);

  useEffect(() => {
    if (!mapReady) return;

    const content = svgWrapper.current?.querySelector('#content');
    if (!content) return;

    // Prevents duplicates when readerVectors changes.
    readerVectors.forEach((vector) => {
      const temp = document.createElement('div');
      temp.innerHTML = vector.trim();
      const svgNode = temp.firstElementChild;
      if (!svgNode?.id) return;

      const existing = content.querySelector(`#${CSS.escape(svgNode.id)}`);
      if (existing) existing.remove();
    });

    readerVectors.forEach((vector) => addReaderToMap(vector));

    setReadersReady(true);
  }, [mapReady, readerVectors, addReaderToMap]);

  useEffect(() => {
    if (!mapReady || !svgWrapper.current) return;

    const svgRoot = svgWrapper.current.querySelector('svg');
    if (!svgRoot) return;

    const svgSelection = d3.select(svgRoot);

    zoom.on('zoom', handleZoom);
    svgSelection.call(zoom);

    return () => {
      // Remove zoom listeners when the component remounts or map rebuilds.
      svgSelection.on('.zoom', null);
    };
  }, [mapReady, zoom, handleZoom]);

  useEffect(() => {
    const highlightSelectedReader = (reader) => {
      if (!reader || !svgWrapper.current) return;

      const unselected = rfidReaders.filter((curr) => curr !== reader);

      unselected.forEach((vector) => {
        const readerSVG = document.getElementById(`${vector}-SVG`);
        const ellipse = readerSVG?.querySelector('ellipse');

        if (ellipse) {
          ellipse.style.fill = '#C00000';
        }
      });

      const selectedReader = document.getElementById(`${reader}-SVG`);
      const selectedEllipse = selectedReader?.querySelector('ellipse');

      if (selectedEllipse) {
        selectedEllipse.style.setProperty(
          'fill',
          'rgb(61, 112, 214)',
          'important',
        );
      }
    };

    if (mapReady && readersReady && selectActiveReader && storeId !== 'T7820') {
      highlightSelectedReader(selectActiveReader);
      focusOnReader(selectActiveReader);
    }
  }, [
    mapReady,
    readersReady,
    selectActiveReader,
    storeId,
    rfidReaders,
    focusOnReader,
  ]);

  return (
    <>
      <div
        data-testid="neptuneMap"
        className="mapWrapper"
        ref={svgWrapper}
      ></div>
      {direction !== '' && calibrationState === 'DONE' && (
        <div className="directionResults">
          <p className="hc-ta-center">
            <strong>{direction}</strong> EVENT{' '}
            <span>
              {direction === 'OUTSIDE' ? (
                <EnterpriseIcon size="md" icon={ChevronDownDoubleIcon} />
              ) : (
                <EnterpriseIcon size="md" icon={ChevronUpDoubleIcon} />
              )}
            </span>
          </p>
        </div>
      )}
    </>
  );
};
